import { rem, Select } from '@mantine/core';
import { GridApi } from 'ag-grid-community';
import useFilterValue, { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { useEffect, useMemo, useState } from 'react';

interface RemoteSource {
  remoteSourceId: number;
  name: string;
}

/**
 * The key of the filter. Must be equal to the column field.
 */
export const FILTER_KEY = 'remoteSource.remoteSourceId' as const;

/**
 * Parameters of the RemoteSourceSelectFilter component.
 */
export interface RemoteSourceSelectFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
}

/**
 * Filter component for the remote source column.
 *
 * This filter assumes:
 *   - column field is `remoteSource.remoteSourceId`
 */
export default function RemoteSourceSelectFilter({ api, onChange }: RemoteSourceSelectFilterProps) {
  const [value, setValue] = useFilterValue({ api, filterKey: FILTER_KEY, onChange });
  const [remoteSources, setRemoteSources] = useState<RemoteSource[]>([]);

  const options = useMemo(
    () =>
      remoteSources
        .map(({ remoteSourceId, name }) => ({ value: String(remoteSourceId), label: name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [remoteSources]
  );

  useEffect(() => {
    /** Finds unique remote sources and sets them as options. */
    const findUniqueRemoteSources = () => {
      const uniqueRemoteSources = new Map<number, RemoteSource>();

      api.forEachNode(({ data: { remoteSource } }) => {
        if (remoteSource && !uniqueRemoteSources.has(remoteSource.remoteSourceId)) {
          uniqueRemoteSources.set(remoteSource.remoteSourceId, remoteSource);
        }
      });

      setRemoteSources(Array.from(uniqueRemoteSources.values()));
    };

    findUniqueRemoteSources();

    api.addEventListener('modelUpdated', findUniqueRemoteSources);
    return () => api.removeEventListener('modelUpdated', findUniqueRemoteSources);
  }, [api]);

  return (
    <Select
      data={options}
      clearable
      w="100%"
      size="sm"
      placeholder="Všetky externé zdroje"
      value={String(value)}
      onChange={setValue}
      nothingFound="Nenašli sa žiadne externé zdroje"
      styles={{ dropdown: { minWidth: rem(240) } }}
    />
  );
}
